import { useEffect, useRef, useState } from "react";
import { channelNav, searchHot } from "@/mocks/home";
import useClock from "@/hooks/useClock";

export default function Masthead() {
  const { date, time } = useClock();
  const [active, setActive] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [kw, setKw] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  return (
    <header className="relative z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      {/* utility strip */}
      <div className="hidden border-b border-background-200/80 lg:block">
        <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between px-6 py-1.5 text-xs text-foreground-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {date}
            </span>
            <span className="flex items-center gap-1.5 tabular-nums">
              <i className="ri-time-line" />
              {time}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="mr-1 inline-flex items-center gap-1.5 text-foreground-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary-500" />
              编辑部值班中 · 线索持续跟进
            </span>
            {["设为首页", "收藏本站"].map((t) => (
              <a
                key={t}
                href="#"
                onClick={(e) => e.preventDefault()}
                className="cursor-pointer px-2 py-1 transition-colors hover:text-primary-600"
              >
                {t}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* masthead */}
      <div className="mx-auto flex w-full max-w-[1280px] items-center justify-between gap-4 px-4 py-4 md:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-eye-2-line text-xl" />
          </div>
          <div className="leading-none">
            <a href="/" className="block">
              <h1 className="whitespace-nowrap font-heading text-3xl font-black tracking-wide text-foreground-950 md:text-4xl">
                看黑料
              </h1>
            </a>
            <p className="mt-1 whitespace-nowrap text-[11px] tracking-[0.2em] text-foreground-400">
              全网新鲜内幕 · 看黑料今日曝光
            </p>
          </div>
        </div>

        <div className="hidden flex-col items-end text-right lg:flex">
          <span className="flex items-center gap-1.5 text-xs text-foreground-500">
            <i className="ri-flashlight-fill text-primary-500" />
            最新黑料新爆料 · 持续更新
          </span>
          <span className="mt-1 flex items-center gap-1.5 text-[11px] text-foreground-400">
            <i className="ri-refresh-line" />
            距上一条更新
            <span className="tabular-nums text-primary-600">00:00:18</span>
          </span>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => setSearchOpen((s) => !s)}
            aria-label="搜索"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-600"
          >
            <i className="ri-search-line text-lg" />
          </button>
          <a
            href="#publish"
            onClick={(e) => e.preventDefault()}
            className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-700 transition-colors hover:bg-primary-100 sm:flex"
          >
            <i className="ri-edit-2-line" />
            发布爆料
          </a>
          <a
            href="#download"
            className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:flex"
          >
            <i className="ri-smartphone-line" />
            下载客户端
          </a>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((m) => !m)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 lg:hidden"
          >
            <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
          </button>
        </div>
      </div>

      {/* channel nav */}
      <nav
        className={`border-t border-background-200/80 transition-shadow ${scrolled ? "bg-background-50/95 shadow-[0_1px_0_0_oklch(var(--background-200))] backdrop-blur" : ""}`}
      >
        <div className="mx-auto flex w-full max-w-[1280px] items-center gap-1 overflow-x-auto px-4 no-scrollbar md:px-6">
          {channelNav.map((c, i) => (
            <a
              key={c.label}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setActive(i);
              }}
              className={`relative flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-3 text-sm transition-colors ${
                active === i
                  ? "font-semibold text-primary-600"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              {c.label}
              {c.hot ? (
                <span className="rounded-sm bg-primary-500 px-1 py-px text-[10px] font-semibold text-background-50">
                  实时
                </span>
              ) : null}
              {active === i ? (
                <span className="absolute inset-x-3 bottom-0 h-0.5 bg-primary-500" />
              ) : null}
            </a>
          ))}
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className="ml-auto hidden shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap px-3 py-3 text-sm text-foreground-500 hover:text-primary-600 md:flex"
          >
            <i className="ri-more-2-fill" />
            更多
          </a>
        </div>
      </nav>

      {/* mobile menu panel */}
      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <div className="flex flex-wrap gap-2">
            {channelNav.map((c) => (
              <a
                key={c.label}
                href="#"
                onClick={(e) => e.preventDefault()}
                className="cursor-pointer rounded-md bg-background-100 px-3 py-1.5 text-sm text-foreground-700"
              >
                {c.label}
              </a>
            ))}
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <a
              href="#publish"
              onClick={(e) => e.preventDefault()}
              className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-700"
            >
              <i className="ri-edit-2-line" />
              发布爆料
            </a>
            <a
              href="#download"
              className="flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2 text-sm font-medium text-background-50"
            >
              <i className="ri-smartphone-line" />
              下载客户端
            </a>
          </div>
        </div>
      ) : null}

      {/* search panel */}
      {searchOpen ? (
        <div className="border-t border-background-200 bg-background-100/70">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-5 md:px-6">
            <form
              className="mx-auto flex max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-1.5"
              onSubmit={(e) => e.preventDefault()}
            >
              <i className="ri-search-line text-foreground-400" />
              <input
                ref={searchRef}
                value={kw}
                onChange={(e) => setKw(e.target.value)}
                placeholder="搜一搜今日曝光 / 实时动态 / 人物关键词"
                className="flex-1 bg-transparent py-2 text-sm outline-none placeholder:text-foreground-300"
              />
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
            <div className="mx-auto mt-3 flex max-w-2xl flex-wrap items-center gap-2 text-xs text-foreground-400">
              <span className="whitespace-nowrap">大家都在搜</span>
              {searchHot.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setKw(s)}
                  className="cursor-pointer rounded-full bg-background-50 px-2.5 py-1 text-foreground-500 transition-colors hover:bg-primary-50 hover:text-primary-600"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}